const RECAP_HEADINGS = {
  'what went well': 'What Went Well',
  'where the time is': 'Where the Time Is',
  'largest gaps to your proven pace': 'Largest Gaps to Your Proven Pace',
  'the big picture': 'The Big Picture',
  'next session focus': 'Next Session Focus',
};

// Defaults only — the running choice comes from the server (GET /api/config,
// OLLAMA_COACH_MODEL), so switching models is a backend setting rather than a
// rebuild. These stay as the answer for when /config is unreachable.
export const PREFERRED_RECAP_MODEL = 'mistral-small3.2:24b';
const PREFERRED_FALLBACK_MODEL = 'llama3.1:8b';

export function pickRecapModel(models, served = {}) {
  const preferredName = served.model || PREFERRED_RECAP_MODEL;
  const fallbackName = served.fallbackModel || PREFERRED_FALLBACK_MODEL;
  const preferred = (models || []).find(
    model => model.name.startsWith(preferredName),
  );
  const fallback = (models || []).find(
    model => model.name.startsWith(fallbackName),
  );
  return preferred?.name || fallback?.name || models?.[0]?.name || null;
}

export function sessionRecapErrorMessage(error) {
  return /(No scored laps|404)/i.test(error || '')
    ? 'This session has no scored laps yet — the recap needs the session to be on the leaderboard.'
    : 'The AI recap is unavailable right now. Check that Ollama is running and try again.';
}

function headingName(line) {
  const normalized = line
    .trim()
    .replace(/^#{1,4}\s*/, '')
    .replace(/^\*\*(.+)\*\*$/, '$1')
    .trim()
    .toLowerCase();
  return RECAP_HEADINGS[normalized] || null;
}

export function narrativeRecapSections(text) {
  const found = new Map();
  let active = null;
  let body = [];

  const flush = () => {
    if (active && body.join('\n').trim()) {
      found.set(active, `## ${active}\n${body.join('\n').trim()}`);
    }
    body = [];
  };

  for (const line of (text || '').split('\n')) {
    const heading = headingName(line);
    if (heading) {
      flush();
      active = heading;
    } else if (active) {
      body.push(line);
    } else if (line.trim()) {
      // Keep partially streamed, headerless prose useful rather than hiding it.
      active = 'What Went Well';
      body.push(line);
    }
  }
  flush();
  return found;
}

export function composeSessionRecapSlots(text, whereTimeMarkdown,
                                         nextSessionFocusMarkdown,
                                         carHealthMarkdown = null) {
  const narrative = narrativeRecapSections(text);
  const provenPaceMarkdown = whereTimeMarkdown?.replace(
    /^#{1,4}\s+[^\n]+/,
    '## Largest Gaps to Your Proven Pace',
  );
  const slots = [
    { id: 'went-well', title: 'What Went Well',
      markdown: narrative.get('What Went Well') || null },
    { id: 'big-picture', title: 'The Big Picture',
      markdown: narrative.get('The Big Picture') || null },
    { id: 'where-time', title: 'Largest Gaps to Your Proven Pace',
      markdown: provenPaceMarkdown || null },
    { id: 'next-focus', title: 'Next Session Focus',
      markdown: nextSessionFocusMarkdown || null },
  ];
  // Python-owned and never streamed, so it is only a slot when it exists —
  // a null here must not render a "Generating…" placeholder.
  if (carHealthMarkdown) {
    slots.push({ id: 'car-health', title: 'Car Health', markdown: carHealthMarkdown });
  }
  return slots;
}

export function composeSessionRecapSections(text, whereTimeMarkdown,
                                            nextSessionFocusMarkdown) {
  if (!whereTimeMarkdown || !nextSessionFocusMarkdown) return null;
  return composeSessionRecapSlots(text, whereTimeMarkdown, nextSessionFocusMarkdown)
    .map(slot => slot.markdown)
    .filter(Boolean);
}
